import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  Icon,
  Box,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  InfoIcon,
  TriangleUpIcon,
  TriangleDownIcon,
} from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import ModalDictamenPC from './ModalDictamenPC';
import ModalVerLicencia from './ModalVerLicencia';

interface Licencia {
  Id: number;
  UsuarioId: number;
  Folio: string | null;
  Ciudadano: string;
  Giro: string | null;
  Tipo: string | null;
  Movimiento: string | null;
  Dictamen: string | null;
  Requisitos: string | null;
  Estatus: string | null;
  Pago: string | null;
  UltimoPago: string | null;
  Vencimiento: string | null;
  Monto: number | null;
  Observaciones: string | null;
  FechaSolicitud: string;
}

const TablaLicencias = () => {
  const [licencias, setLicencias] = useState<Licencia[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: keyof Licencia; direction: 'asc' | 'desc' } | null>(null);
  const { isOpen: isDictamenOpen, onOpen: onDictamenOpen, onClose: onDictamenClose } = useDisclosure();
  const { isOpen: isVerOpen, onOpen: onVerOpen, onClose: onVerClose } = useDisclosure();
  const [licenciaSeleccionada, setLicenciaSeleccionada] = useState<Licencia | null>(null);

  useEffect(() => {
    const fetchLicencias = async () => {
      try {
        const res = await fetch('http://52.91.130.91:5000/api/Solicitudes');
        const data = await res.json();
        setLicencias(data);
      } catch (error) {
        console.error('Error al cargar licencias:', error);
      }
    };

    fetchLicencias();
  }, []);

  const requestSort = (key: keyof Licencia) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedLicencias = [...licencias].sort((a, b) => {
    if (!sortConfig) return 0;

    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (aVal === null) return 1;
    if (bVal === null) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortConfig.direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });

  const getSortIcon = (key: keyof Licencia) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <TriangleUpIcon ml={1} /> : <TriangleDownIcon ml={1} />;
  };

  const getEstatusIcon = (estatus: string | null) => {
    switch (estatus) {
      case 'Activa':
        return <Icon as={CheckCircleIcon} color="green.400" />;
      case 'En revisión':
        return <Icon as={TimeIcon} color="orange.400" />;
      case 'Suspendida':
        return <Icon as={WarningIcon} color="red.500" />;
      default:
        return <Icon as={InfoIcon} />;
    }
  };

  const getPagoBadge = (estado: string | null) => {
    switch (estado) {
      case 'Pagado':
        return <Badge colorScheme="green">{estado}</Badge>;
      case 'Pendiente':
        return <Badge colorScheme="red">{estado}</Badge>;
      case 'Parcial':
        return <Badge colorScheme="orange">{estado}</Badge>;
      default:
        return <Badge>{estado ?? '—'}</Badge>;
    }
  };

  return (
    <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white" p={4}>
      <Table variant="simple" size="sm">
        <Thead bg="#A1263D">
          <Tr>
            <Th color="white" cursor="pointer" onClick={() => requestSort('Folio')}>Folio {getSortIcon('Folio')}</Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('Ciudadano')}>Ciudadano {getSortIcon('Ciudadano')}</Th>
            <Th color="white">Giro</Th>
            <Th color="white">Tipo</Th>
            <Th color="white">Movimiento</Th>
            <Th color="white">Dictamen</Th>
            <Th color="white">Requisitos</Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('Estatus')}>Estatus {getSortIcon('Estatus')}</Th>
            <Th color="white">Pago</Th>
            <Th color="white">Último Pago</Th>
            <Th color="white">Vencimiento</Th>
            <Th color="white" textAlign="center">Acciones</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedLicencias.map((lic) => (
            <Tr key={lic.Id}>
              <Td>{'LIC-'+lic.Id ?? '- - -'}</Td>
              <Td>{lic.Ciudadano}</Td>
              <Td>{lic.Giro ?? '- - -'}</Td>
              <Td>{lic.Tipo ?? '- - -'}</Td>
              <Td>{lic.Movimiento ?? '- - -'}</Td>
              <Td>
                <Badge colorScheme={
                  lic.Dictamen === 'Aprobado'
                    ? 'green'
                    : lic.Dictamen === 'Pendiente'
                    ? 'orange'
                    : 'red'
                }>
                  {lic.Dictamen ?? '- - -'}
                </Badge>
              </Td>
              <Td>
                <Badge colorScheme={
                  lic.Requisitos === 'Completos'
                    ? 'green'
                    : 'orange'
                }>
                  {lic.Requisitos ?? '- - -'}
                </Badge>
              </Td>
              <Td>
                <Flex align="center" gap={2}>
                  {getEstatusIcon(lic.Estatus)}
                  {lic.Estatus ?? '- - -'}
                </Flex>
              </Td>
              <Td>{getPagoBadge(lic.Pago)}</Td>
              <Td>{lic.FechaSolicitud ? lic.FechaSolicitud.split('T')[0] : '—'}</Td>
              <Td>{lic.Vencimiento ? lic.Vencimiento.split('T')[0] : '—'}</Td>
              <Td>
                <Flex justify="center" gap={2}>
                  <Button
                    size="xs"
                    colorScheme="purple"
                    onClick={() => {
                      setLicenciaSeleccionada(lic);
                      onDictamenOpen();
                    }}
                  >
                    Dictamen PC
                  </Button>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    onClick={() => {
                      setLicenciaSeleccionada(lic);
                      onVerOpen();
                    }}
                  >
                    Ver
                  </Button>
                  <Button size="xs" colorScheme="green">PDF</Button>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* {licenciaSeleccionada && (
        <>
          <ModalDictamenPC
            isOpen={isDictamenOpen}
            onClose={onDictamenClose}
            licenciaSeleccionada={licenciaSeleccionada}
          />
          <ModalVerLicencia
            isOpen={isVerOpen}
            onClose={onVerClose}
            licencia={licenciaSeleccionada}
          />
        </>
      )} */}
    </Box>
  );
};

export default TablaLicencias;
